import { useState } from 'react';
import { createHashRouter, Navigate, useLocation, Outlet } from 'react-router-dom';
import AuthLayout from '@Root/AuthLayout';
import ErrorFallBack from '@Root/ErrorFallBack';
import LandingPage from '@LandingPages/LandingPage';
import LoginPage from '@LandingPages/LoginPage';
import OnboardingPage from '@LandingPages/OnboardingPage';
import HomePage from '@LandingPages/HomePage';
import LogoutPage from '@LandingPages/LogoutPage';
import SignupPage from '@LandingPages/SignupPage';
import TeamsPage from '@OrganizationPages/TeamsPage';
import MembersPage from '@OrganizationPages/MembersPage';
import DashboardPage from '@Pages/DashboardPage';
import LeadsPage from '@Pages/LeadsPage';
import TrackingPage from '@Pages/TrackingPage';
import ContactsPage from '@RegistriesPages/ContactsPage';
import RegistriesImportForm from '@RegistriesForms/RegistriesImportForm';
import EmailCampaignsPage from '@Pages/EmailCampaignsPage';
import EmailsPage from '@Pages/EmailsPage';
import WhatsappDataLoader from '@Whatsapp/WhatsappDataLoader';
import WhatsappCampaignsView from '@ComponentViews/WhatsappCampaignsView';
import WhatsAppChatsView from '@ComponentViews/WhatsAppChatsView';
import RecentItems from '@ComponentViews/RecentItems';
import WhatsAppTemplateView from '@ComponentViews/WhatsAppTemplateView';
import BusinessInfoPage from '@Pages/BusinessInfoPage';
import AIAgentPage from '@Pages/AIAgentPage';
import AutoRepliesPage from '@Pages/AutoRepliesPage';
import WhatsappCampaignStatusPage from '@Pages/WhatsappCampaignStatusPage';
import CampaignForm from '@FormForms/CampaignForm';
import UpdateCampaignForm from '@FormForms/UpdateCampaignForm';
import ContactDetailsPage from '@CommonFunctionalDisplay/ContactDetailsPage';
import WhatsappLeadsSettingsPage from '@Pages/WhatsappLeadsSettingsPage';
import ForgotPasswordPage from '@LandingPages/ForgotPasswordPage';
import ProfilePage from '@UserManagementPages/ProfilePage';
import SettingsPage from '@UserManagementPages/SettingsPage';
import WhatsappConnectForm from '@FormForms/WhatsappConnectForm';
import FacebookOAuthPage from '@WhatsappOnboard/FacebookOAuthPage';
import FacebookOAuthCallBack from '@WhatsappOnboard/FacebookOAuthCallBack';
import { useReduxProject } from '@Hooks/reduxProjectHook/useReduxProject';
import { useReduxToken } from '@Hooks/reduxTokenHook/useReduxToken';
import { useReduxLeads } from '@Hooks/reduxLeadsHook/useReduxLeads';

const renderLandingPages = (param, setToken, setEntryPage) => {

    switch (param) {
        case 'login':
            return <LoginPage setToken={setToken} setEntryPage={setEntryPage} />;
        case 'signup':
            return <SignupPage setToken={setToken} setEntryPage={setEntryPage} />;
        case 'forgotPassword':
            return <ForgotPasswordPage setEntryPage={setEntryPage} />;
        default:
            return <LoginPage setToken={setToken} setEntryPage={setEntryPage} />;
  }

};

const LoginPageWrapper = () => {

//    useThemeChange();
    const location = useLocation();
    const { token, setToken } = useReduxToken();
    const [entryPage, setEntryPage] = useState('login');
    const from = location.state?.from?.pathname || '/home';
    if(!!token) {
        if (entryPage === 'signupSuccess') {
            return <Navigate to='/onboarding' replace />;
        }
        return <Navigate to={from} />;
    }
    return renderLandingPages(entryPage, setToken, setEntryPage);
};

const RouteMismatchWrapper = ()=> {

    const { token } = useReduxToken();
    const location = useLocation();

    if(!!token) {
        return <Navigate to='/home' state={{ from: location }} replace />;
    }
    return <Navigate to='/logout' state={{ from: location }} replace />;
}

const WhatsappPageWrapper = ()=> {

    const { currentLeads } = useReduxLeads();

    if(!!currentLeads) {
        return <WhatsappDataLoader />;
    }
    return <FacebookOAuthPage />;
}

const OnboardingWrapper = ()=> {

    const location = useLocation();
    const { currentProject } = useReduxProject();
    const { token } = useReduxToken();

    if(!!currentProject) {
        return <Navigate to='/home/leads' state={{ from: location }} replace />;
    }
    if(!!token) {
        return <OnboardingPage />;
    }
    return <Navigate to='/' state={{ from: location }} replace />;
}

const Routes = createHashRouter([
    {
        path: '/',
        element: <LandingPage />,
    },
    {
        path: 'login',
        element: <LoginPageWrapper />,
    },
    {
        path: 'logout',
        element: <LogoutPage />
    },
    {
        path: 'onboarding',
        element: <OnboardingWrapper />
    },
    {
        path: 'home',
        element: <AuthLayout />,
        errorElement: <ErrorFallBack />,
        children: [
            {
                index: true,
                element: <DashboardPage />,
            },
            {
                path: 'emails',
                element: <EmailsPage />
            },
            {
                path: 'leads',
                element: <LeadsPage />
            },
            {
                path: 'tracking',
                element: <TrackingPage />
            },
            {
                path: 'callback',
                element: <FacebookOAuthCallBack />
            },
            {
                path: 'whatsapp',
                element: <WhatsappPageWrapper />,
                children: [
                    {
                        index: true,
                        element: <WhatsAppChatsView />
                    },
                    {
                        path: 'campaigns',
                        children: [
                            {
                                index: true,
                                element: <WhatsappCampaignsView />
                            },
                            {
                                path: 'create',
                                element: <CampaignForm itemType='whatsapp'/>
                            },
                            {
                                path: ':campaignId',
                                children: [
                                    {
                                        index: true,
                                        element: <WhatsappCampaignStatusPage />
                                    },
                                    {
                                        path: ':action',
                                        element: <CampaignForm itemType='whatsapp'/>
                                    }
                                ]
                            },
                        ],
                    },
                    {
                        path: 'templates',
                        element: <WhatsAppTemplateView />
                    },
                    {
                        path: 'auto-replies',
                        element: <AutoRepliesPage />
                    },
                    {
                        path: 'business-info',
                        element: <BusinessInfoPage />
                    },
                    {
                        path: 'ai-agent',
                        element: <AIAgentPage />
                    },
                    {
                        path: 'settings',
                        element: <WhatsappLeadsSettingsPage />
                    }
                ]
            },
            {
                path: 'contact-detail/:nameCardId/:campaignJobId?',
                element: <ContactDetailsPage />
            },
            {
                path: 'files',
                element: <RecentItems />
            },
            {
                path: 'contacts',
                children: [
                    {
                        index: true,
                        element: <ContactsPage />
                    },
                    {
                        path: 'import',
                        element: <RegistriesImportForm registryType='nameCard'/>
                    },
                ]
            },
            {
                path: 'teams',
                element:  <TeamsPage />,
            },
            {
                path: 'agents',
                element:  <MembersPage />,
            },
            {
                path: 'userManagement',
                children: [
                    {
                        index: true,
                        element: <ProfilePage/>,
                    },
                    {
                        path: 'settings',
                        element: <SettingsPage/>,
                    },
                ],
            },
        ],
    },
    {
        path: '*',
        element: <RouteMismatchWrapper />,
    },
]);

export default Routes;